import { FileText } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Modal } from '@/components/ui/modal'
import { Skeleton } from '@/components/ui/skeleton'
import { Textarea } from '@/components/ui/textarea'
import { CheckRow, Field } from '@/components/common/FormBits'
import { useQuoteTemplate, useSaveQuoteTemplate, type QuoteTemplate } from '../../quotes'
import { toastError } from '../../ui'

/** Teklif şablonu: belge başlığı, giriş, koşullar, altbilgi, e-posta konusu/metni ve yönetici iskonto sınırı. */
export function QuoteTemplateButton() {
  const { t } = useTranslation()
  const [open, setOpen] = useState(false)
  return (
    <>
      <Button variant="outline" onClick={() => setOpen(true)}>
        <FileText />
        {t('crm.quote.template')}
      </Button>
      {open ? <TemplateModal onClose={() => setOpen(false)} /> : null}
    </>
  )
}

function TemplateModal({ onClose }: { onClose: () => void }) {
  const { t } = useTranslation()
  const q = useQuoteTemplate()
  return (
    <Modal open size="xl" onOpenChange={(o) => !o && onClose()} icon={<FileText />} title={t('crm.quote.template')} description={t('crm.quote.templateHint')}>
      {q.data ? <Form initial={q.data} onClose={onClose} /> : <Skeleton className="h-64" />}
    </Modal>
  )
}

function Form({ initial, onClose }: { initial: QuoteTemplate; onClose: () => void }) {
  const { t } = useTranslation()
  const save = useSaveQuoteTemplate()
  const [v, setV] = useState(initial)
  const mgr = Number(v.managerMaxDiscount.replace(',', '.'))
  const invalid = !v.title.trim() || !v.mailSubject.trim() || !v.mailBody.trim() || !(mgr >= 0 && mgr <= 100)
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <Field id="qt-title" label={t('crm.quote.tpl.title')}>
          <Input id="qt-title" value={v.title} maxLength={120} onChange={(e) => setV({ ...v, title: e.target.value })} />
        </Field>
        <Field id="qt-mgr" label={t('crm.quote.tpl.managerMax')} hint={t('crm.quote.tpl.managerMaxHint')}>
          <Input id="qt-mgr" inputMode="decimal" value={v.managerMaxDiscount} onChange={(e) => setV({ ...v, managerMaxDiscount: e.target.value })} />
        </Field>
      </div>
      <Field id="qt-intro" label={t('crm.quote.tpl.intro')}>
        <Textarea id="qt-intro" rows={2} value={v.intro} onChange={(e) => setV({ ...v, intro: e.target.value })} />
      </Field>
      <Field id="qt-terms" label={t('crm.quote.tpl.terms')}>
        <Textarea id="qt-terms" rows={3} value={v.terms} onChange={(e) => setV({ ...v, terms: e.target.value })} />
      </Field>
      <Field id="qt-footer" label={t('crm.quote.tpl.footer')}>
        <Input id="qt-footer" value={v.footer} maxLength={300} onChange={(e) => setV({ ...v, footer: e.target.value })} />
      </Field>
      <Field id="qt-subject" label={t('crm.quote.tpl.mailSubject')} hint={t('crm.quote.tpl.vars')}>
        <Input id="qt-subject" value={v.mailSubject} maxLength={200} onChange={(e) => setV({ ...v, mailSubject: e.target.value })} />
      </Field>
      <Field id="qt-body" label={t('crm.quote.tpl.mailBody')} hint={t('crm.quote.tpl.vars')}>
        <Textarea id="qt-body" rows={6} value={v.mailBody} onChange={(e) => setV({ ...v, mailBody: e.target.value })} />
      </Field>
      <CheckRow checked={v.showSignatures} onChange={(e) => setV({ ...v, showSignatures: e.target.checked })}>
        {t('crm.quote.tpl.signatures')}
      </CheckRow>
      <div className="flex justify-end gap-2 border-t pt-3">
        <Button variant="cancel" onClick={onClose}>
          {t('common.cancel')}
        </Button>
        <Button
          disabled={invalid || save.isPending}
          onClick={() =>
            save.mutate(v, {
              onSuccess: () => {
                toast.success(t('crm.quote.templateSaved'))
                onClose()
              },
              onError: toastError,
            })
          }
        >
          {t('common.save')}
        </Button>
      </div>
    </div>
  )
}
